/**
 * Debounced server-side autosave. Mirrors the XState snapshot to the backend during play (~1.5s
 * debounce) and immediately on finish, best-effort — the local Zustand autosave is the instant,
 * offline-safe fallback. Run id = the local run id (see store/session.ts). Only used in authed routes.
 */
import { useEffect } from "react";
import type { ActorRefFrom } from "xstate";
import type { gameMachine } from "./machine";
import { useSession } from "../store/session";
import { autosaveRun, type AutosavePayload } from "../api/runs";

export function useServerAutosave(actor: ActorRefFrom<typeof gameMachine>): void {
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const sub = actor.subscribe((snap) => {
      if (snap.matches("briefing")) return; // nothing to persist yet
      const runId = useSession.getState().runId;
      if (!runId) return;
      const finished = snap.matches("finished");
      const payload: AutosavePayload = {
        snapshot: actor.getPersistedSnapshot(),
        status: finished ? "finished" : "in_progress",
        mode: snap.context.mode,
        seed: snap.context.seed,
        scenarioIndex: snap.context.index,
        endingId: snap.context.endingId,
        scores: snap.context.scores,
        transcript: snap.context.transcript as unknown[],
      };
      clearTimeout(timer);
      const send = () => void autosaveRun(runId, payload).catch(() => undefined);
      if (finished) send();
      else timer = setTimeout(send, 1500);
    });
    return () => {
      clearTimeout(timer);
      sub.unsubscribe();
    };
  }, [actor]);
}
